import type { RcFile } from 'antd/lib/upload';
import type { Argument } from 'classnames';

import type { DocumentMedia } from 'modules/medias/types/document-media';
import type { ImageMedia } from 'modules/medias/types/image-media';
import type { Video360Media } from 'modules/medias/types/video-360-media';
import type { VideoMedia } from 'modules/medias/types/video-media';

export const MediaSizeEnum = {
  DEFAULT: 'default',
  SMALL: 'small',
  THUMBNAIL: 'thumbnail'
} as const;

export type MediaSizeEnum = (typeof MediaSizeEnum)[keyof typeof MediaSizeEnum];

export type MediaItem = VideoMedia | ImageMedia | DocumentMedia | Video360Media;

export const MediaListItemStatus = {
  LOADING: 'LOADING',
  LOADED: 'LOADED',
  FAILED: 'FAILED',
  OK: 'OK'
} as const;

export type MediaListItemStatus = (typeof MediaListItemStatus)[keyof typeof MediaListItemStatus];

export type MediaListItem<U extends MediaItem = MediaItem> = {
  data: U;
  status: MediaListItemStatus;
  id?: string;
  uuid?: string;
  file?: File;
  order?: number;
  isUploaded?: boolean;
  uploadedId?: string;
  metadata?: { origin: string };
};

export type MediaGalleryActions = {
  onPreview?: (mediaListItem: MediaListItem) => void;
  onDelete?: (mediaListItem: MediaListItem) => void;
  onRestrict?: (mediaListItem: MediaListItem) => void;
  onDownload?: (mediaListItem: MediaListItem) => void;
  onRetry?: (mediaListItem: MediaListItem) => void;
  onClipping?: (mediaId: string) => void;
  beforeUpload?: (file: RcFile, FileList: RcFile[]) => boolean | PromiseLike<void>;
  disabledOnDownload?: boolean;
  disabledOnDelete?: boolean;
  disabledOnRestrict?: boolean;
  showClipping?: boolean;
};

type MediaProps = {
  index: number;
  media: MediaListItem;
  hideQuickDelete?: boolean;
  className?: Argument;
} & MediaGalleryActions;

export type MediaGalleryProps = {
  mediaList: MediaListItem[];
  groupedByType?: boolean;
  mediaSize?: MediaSizeEnum;
  hideTitle?: boolean;
  hideDownload?: boolean;
  withPreview?: boolean;
  hideSection?: boolean;
  className?: Argument;
  renderMedia?: (props: MediaProps) => React.ReactNode;
} & MediaGalleryActions;

export type GroupedMediaListProps = {
  groupBy?: (collection: MediaListItem[]) => Record<string, MediaListItem[]>;
  hideTitle?: boolean;
} & MediaGalleryProps;
